import React, { createContext, useEffect, Suspense } from 'react';
import { useNavigate, useParams, useSearchParams, Outlet } from 'react-router';
import i18n from '@lib/i18n';

import {
  AutoLogout,
  Core,
  ChakraUIProvider,
  HeaderMenuWrapper,
  ProgressSpinner,
  Sidebar,
  SystemNotifications,
  useBreakpointRef,
  PageLoadAnnouncer,
} from '@everfi/reactifi';
// Note: useBreakpointRef drives the viewport-aware 'New View' label in the header — keep.
import { useTheme } from '@everfi/design-system';

import environment from '../environment';
import { useTokenStore } from '../authentication/TokenStore';

import { useOrgData } from './useOrgData';
import { useLanguages } from './useLanguages';
import { useOrgBranding } from './useOrgBranding';
import { useUserData } from './useUserData';
import { useSidebarViewOptions } from './useSidebarViewOptions';
import { useAuthContext } from '../authentication/AuthenticationContext';
import { MENU_SLUGS } from './platformConfig';

const {
  ADMINIFI_URL,
  CERTIFICATE_SERVICE_URL,
  CONTENT_SERVICE_URL,
  COMPLIANCE_URL,
  DASHIFI_URL,
  ENGAGE_URL,
  EVALUFI_URL,
  FOUNDRY_KNOWLEDGE_BASE_URL,
  HOMEROOM_PLATFORM_URL,
  HOMEROOM_ADMIN_URL,
  NEXT_URL,
  NOTIFI_URL,
} = environment;

const MENU_ENVIRONMENT = {
  MENU_DOMAIN_FOR_ADMINIFI: ADMINIFI_URL,
  MENU_DOMAIN_FOR_CONTENT_SERVICE: CONTENT_SERVICE_URL,
  MENU_DOMAIN_FOR_CERTIFICATE_SERVICE: CERTIFICATE_SERVICE_URL,
  MENU_DOMAIN_FOR_COMPLIANCE: COMPLIANCE_URL,
  MENU_DOMAIN_FOR_EVALUFI: EVALUFI_URL,
  MENU_DOMAIN_FOR_ENGAGE: ENGAGE_URL,
  MENU_DOMAIN_FOR_FRONT_END: DASHIFI_URL,
  MENU_DOMAIN_FOR_NOTIFI: NOTIFI_URL,
  MENU_DOMAIN_FOR_NEXT: NEXT_URL,
  MENU_DOMAIN_FOR_HOMEROOM: HOMEROOM_PLATFORM_URL,
  FOUNDRY_KNOWLEDGE_BASE_URL,
  HOMEROOM_ADMIN_URL,
};

export const AppContext = createContext({});

// login code borrowed from https://github.com/kdhttps/appauth-react
// and related blog: https://dev.to/kdhttps/appauth-js-integration-in-react-1m3e
/**
 * @param {{
 *   hideImpersonation?: boolean;
 *   portal: string;
 *   sidebar: boolean;
 *   systemNotifications?: boolean;
 * }} props
 */
const Main = ({ hideImpersonation = false, portal, sidebar, systemNotifications = false }) => {
  const { app, platformConfig } = useAuthContext();
  const {
    usesOrgData,
    themeLibrary,
    isHomeroom,
    sessionUrl,
    orgBySlugUrl,
    signOutUrl,
    userMeUrl,
    localesUrl,
    normalizeLocale,
  } = platformConfig;
  const tokenStore = useTokenStore();
  const { slug } = useParams();
  const BREAKPOINTS = Object.freeze([1080]);
  const BREAKPOINT_VALUES = Object.freeze(['', i18n.t('New View')]);
  useBreakpointRef({ breakpoints: BREAKPOINTS, breakpointValues: BREAKPOINT_VALUES, direction: '' });

  // ── Platform-specific data fetching ─────────────────────────────────────────
  const { orgData } = useOrgData({ slug, tokenStore, orgBySlugUrl, signOutUrl });
  const { languages } = useLanguages({ tokenStore, localesUrl, signOutUrl, normalizeLocale });
  const { userData, currentRuleSetName } = useUserData({ slug, tokenStore, userMeUrl, signOutUrl });

  // ── Side effects ─────────────────────────────────────────────────────────────
  useOrgBranding({ orgData, portal });

  useEffect(() => {
    const portalClass = {
      fit: 'admin',
      customer: 'cportal',
      ceos: 'cportal',
      learner: 'learner',
      teacher: 'teacher',
      student: 'student',
      parent: 'parent',
    };
    document.body.classList.add(portalClass[portal]);
    if (!hideImpersonation && userData?.impersonator_id) {
      document.body.classList.add('impersonating');
    }
    if (!sidebar) {
      // TODO: see if we can change the styles in everfi-material so we don't have to have this class on the body
      document.body.classList.add('no-sidebar');
    }
    return () => {
      if (userData?.impersonator_id) {
        document.body.classList.remove('impersonating');
      }
      if (!sidebar) {
        document.body.classList.remove('no-sidebar');
      }
    };
  }, [portal, sidebar, userData?.impersonator_id, hideImpersonation]);

  // ── Homeroom-only: view switcher (New View / Classic View) ───────────────────
  const { viewOptions, modal } = useSidebarViewOptions({ isHomeroom, userData });

  const contextValues = {
    accessToken: tokenStore.currentToken,
    currentRuleSet: userData?.rule_sets?.find(ruleSet => ruleSet.active) || (userData?.rule_sets || [])[0],
    currentRuleSetName,
    organization: orgData,
    user: userData,
  };

  const handleExitImpersonation = () => {
    tokenStore.clearToken();
  };

  const getInitialLanguage = () => {
    const pathSegments = window.location.pathname.split('/').filter(Boolean);
    const localeFromPath = pathSegments[0];
    // Check if first segment looks like a locale (e.g., "en", "fi-FI", "es-MX")
    if (localeFromPath && /^[a-z]{2}(-[A-Z]{2})?$/i.test(localeFromPath)) {
      return localeFromPath.replace('-', '_');
    }
    return localStorage?.getItem('i18nextLng');
  };

  const hasUserData = !!userData;
  const hasOrgData = usesOrgData && !!orgData;
  const canRender = hasUserData || hasOrgData;

  const { activeThemeDict } = useTheme(themeLibrary);

  const logoOverrideHyperlink = environment.getAuthApp(app)?.headerLogoLinks?.[portal];

  return (
    <AppContext.Provider value={contextValues}>
      <ChakraUIProvider theme={activeThemeDict}>
        <div>
          <Core.SkipNav.Link zIndex="var(--udl-zIndices-popover, 1500)" text={i18n.t('Skip to main content')} />
          <HeaderMenuWrapper
            appendLanguage={false}
            currentRuleSet={contextValues.currentRuleSet}
            currentRuleSetName={contextValues.currentRuleSetName}
            environment={MENU_ENVIRONMENT}
            hideImpersonation={hideImpersonation}
            isDashiFi={true}
            isHomeroom={isHomeroom}
            languages={languages}
            logoOverrideHyperlink={logoOverrideHyperlink}
            onExitImpersonation={handleExitImpersonation}
            organization={orgData}
            selectedLanguage={getInitialLanguage()}
            slug={MENU_SLUGS[portal]?.topbar}
            theme={activeThemeDict}
            user={userData}
            renderAsLegacy={false}
            useSearchParams={useSearchParams}
          />
          {modal}
          {sidebar && (
            <Sidebar
              currentRuleSet={contextValues.currentRuleSet}
              currentRuleSetName={contextValues.currentRuleSetName}
              environment={MENU_ENVIRONMENT}
              organization={orgData}
              slug={MENU_SLUGS[portal]?.sidebar}
              theme={activeThemeDict}
              useNavigate={useNavigate}
              user={userData}
              useSearchParams={useSearchParams}
              options={viewOptions}
            />
          )}

          <AutoLogout sessionUrl={sessionUrl} authToken={tokenStore.currentToken} clearToken={tokenStore.clearToken} />

          {/*
        TODO: Other Elements that need to be added somewhere in here
        - Inactive Org banner
        - tag manager and related alert message
        - Role Picker Modal?
        - Footer?
        */}

          {systemNotifications && (
            <SystemNotifications adminifiUrl={ADMINIFI_URL} userId={userData?.id} authToken={tokenStore.currentToken} />
          )}

          <main id="main" className="p2">
            <Core.SkipNav.Content>
              <div id="content">
                <PageLoadAnnouncer focusMode="none" />
                <Suspense fallback={<ProgressSpinner pageLoader={true} renderAsLegacy={false} />}>
                  {canRender && <Outlet />}
                </Suspense>
              </div>
            </Core.SkipNav.Content>
          </main>
        </div>
      </ChakraUIProvider>
    </AppContext.Provider>
  );
};

Main.displayName = 'Main';

export default Main;
